import React, { useState, useEffect, useRef } from 'react';
import 'lite-youtube-embed/src/lite-yt-embed.css';

// Declare the custom element for TypeScript
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'lite-youtube': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          videoid: string;
          params?: string;
          playlabel?: string;
          title?: string;
          style?: React.CSSProperties;
        },
        HTMLElement
      >;
    }
  }
}

interface YouTubeEmbedProps {
  videoId: string;
  title?: string;
  className?: string;
  playlistId?: string;
  params?: string;
  poster?: 'hqdefault' | 'sddefault' | 'maxresdefault';
}

const YouTubeEmbed: React.FC<YouTubeEmbedProps> = ({
  videoId,
  title = 'YouTube Video',
  className = '',
  playlistId,
  params = '',
  poster = 'sddefault'
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Get the correct thumbnail URL
  const thumbnailUrl = `https://i.ytimg.com/vi_webp/${videoId}/${poster}.webp`;
  const fallbackThumbnailUrl = `https://i.ytimg.com/vi/${videoId}/${poster}.jpg`;
  
  // Handle click to load the video
  const handleClick = () => {
    if (!isLoaded && containerRef.current && iframeRef.current) {
      // Create the correct URL depending on whether it's a playlist or single video
      let videoUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1`;
      
      if (playlistId) {
        videoUrl = `https://www.youtube.com/embed/videoseries?list=${playlistId}&autoplay=1`;
      }
      
      if (params) {
        videoUrl += `&${params}`;
      }
      
      // Set the iframe src to load the video
      iframeRef.current.src = videoUrl;
      iframeRef.current.title = title;
      setIsLoaded(true);
      
      // Add the loaded class
      containerRef.current.classList.add('lyt-activated');
    }
  };

  // Handle thumbnail error (fallback to jpg if webp fails)
  const handleThumbnailError = () => {
    setUseFallback(true);
  };

  // Create the final container class
  const containerClass = `lyt-lite ${className} ${isLoaded ? 'lyt-activated' : ''}`;
  
  // When the thumbnail loads, mark it as loaded
  const handleThumbnailLoad = () => {
    setThumbnailLoaded(true);
  };

  return (
    <div 
      ref={containerRef}
      className={containerClass}
      data-videoid={videoId}
      style={{ 
        backgroundImage: thumbnailLoaded ? `url(${useFallback ? fallbackThumbnailUrl : thumbnailUrl})` : 'none',
        position: 'relative',
        paddingBottom: '56.25%', // 16:9 aspect ratio
        height: 0, 
        overflow: 'hidden',
        maxWidth: '100%',
        backgroundColor: '#000',
        cursor: 'pointer',
        backgroundSize: 'cover',
        backgroundPosition: 'center center'
      }}
      onClick={handleClick}
      role="button"
      aria-label={`Play ${title}`}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {/* Hidden image for preloading the thumbnail */}
      <link rel="preload" as="image" href={thumbnailUrl} />
      <img 
        src={useFallback ? fallbackThumbnailUrl : thumbnailUrl} 
        alt={title}
        style={{ display: 'none' }}
        onError={handleThumbnailError}
        onLoad={handleThumbnailLoad}
        width="480"
        height="360"
      />
      
      {!isLoaded && (
        <div className="lty-playbtn" role="presentation">
          <span className="lty-ui">▶</span>
        </div>
      )}
      
      <iframe
        ref={iframeRef}
        width="560"
        height="315"
        title={title}
        frameBorder="0"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          border: 0,
          display: isLoaded ? 'block' : 'none'
        }}
        loading="lazy"
      ></iframe>
    </div>
  );
};

export default YouTubeEmbed;
